import { BlissSVGBuilder } from "bliss-svg-builder";
import { BlissColorHelper as BColor } from "@blissary/utils";

// Default options for main SVG rendering
// strokeWidth 0.5 and margin 0.75 are rc.1 defaults, so omitted
const DEFAULT_OPTIONS = { grid: true, gridStrokeWidth: 0.16 };

// Compact options for derivation mini SVGs
// Grid defaults to off, so all grid color/stroke settings are unnecessary
const MINI_OPTIONS = { strokeWidth: 0.66, margin: 1 };

// Cropped options for shape search buttons
// height renamed to svgHeight in rc.1; strokeWidth 0.5 is default; grid defaults to off
const SHAPE_BUTTON_OPTIONS = { cropTop: 8, cropBottom: 4, svgHeight: 86, margin: 1 };

/**
 * Creates a simple SVG element with centered text, used for error/missing states.
 */
function createErrorSvg(text, fill, fontSize) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("width", "100");
  svg.setAttribute("height", "100");
  const textEl = document.createElementNS("http://www.w3.org/2000/svg", "text");
  textEl.setAttribute("x", "50");
  textEl.setAttribute("y", "50");
  textEl.setAttribute("text-anchor", "middle");
  textEl.setAttribute("dominant-baseline", "middle");
  textEl.setAttribute("fill", fill);
  textEl.setAttribute("font-size", String(fontSize));
  textEl.textContent = text;
  svg.appendChild(textEl);
  return svg;
}

/**
 * Resolves background options object for the given color scheme and part-of-speech.
 * Returns keys matching the constructor's JS option keys:
 * { background, gridSkyColor, gridEarthColor, gridMajorColor, gridMediumColor, gridMinorColor }
 */
function getBackgroundOptions(colorScheme, pos) {
  if (colorScheme === "Nordic") {
    return BColor.getNordicColorValuesByPos(pos);
  }
  if (colorScheme === "Canadian") {
    return BColor.getCanadianColorValuesByPos(pos);
  }
  return {};
}

/**
 * Build a full-size Bliss SVG element with color-scheme background and grid.
 *
 * @param {string} code           Bliss code string
 * @param {string} pos            Part-of-speech key (e.g. "noun", "verb")
 * @param {string} colorScheme    "Nordic" | "Canadian" | undefined
 * @param {object} [options]      Override default constructor options
 * @param {boolean} [overrideGrid] When true, only the background color is kept from the scheme
 * @returns {SVGElement}
 */
export function buildBlissSvg(code, pos, colorScheme, options, overrideGrid) {
  if (!code) {
    return createErrorSvg("no code", "#999", 12);
  }

  const baseOptions = options || DEFAULT_OPTIONS;
  let bgOptions = getBackgroundOptions(colorScheme, pos);

  if (overrideGrid && bgOptions.background) {
    bgOptions = { background: bgOptions.background };
  }

  try {
    return new BlissSVGBuilder(code, { ...baseOptions, ...bgOptions }).svgElement;
  } catch (error) {
    console.error(`Error rendering Bliss code "${code}":`, error.message);
    return createErrorSvg("\u26a0", "#cc0000", 50);
  }
}

/**
 * Build a compact mini SVG for derivation displays.
 *
 * @param {string} code         Bliss code string
 * @param {string} colorScheme  "Nordic" | "Canadian" | undefined
 * @returns {SVGElement}
 */
export function buildMiniBlissSvg(code, colorScheme) {
  return buildBlissSvg(code, "", colorScheme, MINI_OPTIONS, true);
}

/**
 * Build a cropped SVG for shape search buttons.
 *
 * @param {string} shapeCode  Bliss shape code (before "=>" in shape data)
 * @returns {SVGElement}
 */
export function buildShapeButtonSvg(shapeCode) {
  return new BlissSVGBuilder(shapeCode, SHAPE_BUTTON_OPTIONS).svgElement;
}
